<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- 頁面標題 -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">供款計數機</h4>
        </div>

        <!-- 表單區域 -->
        <b-form @submit.prevent="calculateSchedule">
          <b-row>
            <b-col md="6">
              <b-form-group label="還款週期" label-for="is_monthly">
                <v-select
                  id="is_monthly"
                  v-model="formData.is_monthly"
                  :options="monthlyOptions"
                  :reduce="option => option.id"
                  label="name"
                  placeholder="請選擇還款週期"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="利息計算方式" label-for="interest_type_id">
                <v-select
                  id="interest_type_id"
                  v-model="formData.interest_type_id"
                  :options="interestTypeOptions"
                  :reduce="option => option.id"
                  label="name"
                  placeholder="請選擇利息計算方式"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="貸款額" label-for="amount">
                <b-form-input
                  id="amount"
                  v-model.number="formData.amount"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="請輸入貸款額"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="還款期數" label-for="period">
                <b-form-input
                  id="period"
                  v-model.number="formData.period"
                  type="number"
                  min="1"
                  placeholder="請輸入還款期數"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="年利率 (%)" label-for="annual_interest_rate">
                <b-form-input
                  id="annual_interest_rate"
                  v-model.number="formData.annual_interest_rate"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="請輸入年利率"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="首次供款日期" label-for="first_pay_date">
                <b-form-input
                  id="first_pay_date"
                  v-model="formData.first_pay_date"
                  type="date"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col>
              <b-button
                type="submit"
                variant="primary"
                :disabled="!isFormValid || loading"
                class="mr-2"
              >
                <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                計算
              </b-button>
              <b-button
                variant="outline-secondary"
                @click="resetForm"
                :disabled="loading"
              >
                重置
              </b-button>
            </b-col>
          </b-row>
        </b-form>

        <!-- 計算結果區域 -->
        <div v-if="calculationResult" class="mt-4">
          <hr>
          <h5 class="mb-3">計算結果</h5>
          
          <!-- 統計信息 -->
          <b-row class="mb-3">
            <b-col md="3">
              <b-card class="h-100">
                <b-card-body class="text-center compact-card-body">
                  <h6 class="text-muted mb-1">每期還款額</h6>
                  <h4 class="text-primary mb-0">${{ calculationResult.average_amount }}</h4>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col md="3">
              <b-card class="h-100">
                <b-card-body class="text-center compact-card-body">
                  <h6 class="text-muted mb-1">總利息</h6>
                  <h4 class="text-warning mb-0">${{ totalInterest }}</h4>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col md="3">
              <b-card class="h-100">
                <b-card-body class="text-center compact-card-body">
                  <h6 class="text-muted mb-1">總還款額</h6>
                  <h4 class="text-success mb-0">${{ calculationResult.total_amount }}</h4>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col md="3">
              <b-card class="h-100">
                <b-card-body class="text-center compact-card-body">
                  <h6 class="text-muted mb-1">首次供款日</h6>
                  <h4 class="text-info mb-0">{{ firstPayDate }}</h4>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <!-- 還款明細表 -->
          <div class="">
            <h6 class="mb-2">還款明細</h6>
            <b-table
              :items="calculationResult.schedules"
              :fields="scheduleFields"
              striped
              hover
              responsive
              class="mb-0"
            >
              <template #cell(pay_date)="data">
                {{ formatDate(data.value) }}
              </template>
              <template #cell(initial_principal)="data">
                ${{ Number(data.value).toLocaleString() }}
              </template>
              <template #cell(principal)="data">
                ${{ Number(data.value).toLocaleString() }}
              </template>
              <template #cell(interest)="data">
                ${{ Number(data.value).toLocaleString() }}
              </template>
              <template #cell(payment_amount)="data">
                ${{ Number(data.value).toLocaleString() }}
              </template>
              <template #cell(remaining_principal)="data">
                ${{ Number(data.value).toLocaleString() }}
              </template>
            </b-table>
          </div>

          <!-- 操作按鈕 -->
          <div class="mt-3 d-flex justify-content-end">
            <b-button
              variant="success"
              @click="goToContractCreate"
              :disabled="loading"
            >
              進入合約頁面
            </b-button>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BTable,
  BSpinner
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, onMounted, onUnmounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import contractStoreModule from "@/views/contract/contractStoreModule";
import store from "@/store";

export default {
  name: "LoanCalculator",
  components: {
    BCard,
    BCardBody,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BTable,
    BSpinner,
    vSelect
  },
  setup(props, { root }) {
    const toast = useToast();
    const loading = ref(false);
    const calculationResult = ref(null);
    
    // Store module 管理
    const CONTRACT_APP_STORE_MODULE_NAME = "app-contract";
    
    // 確保 contract store module 已註冊
    onMounted(() => {
      if (!store.hasModule(CONTRACT_APP_STORE_MODULE_NAME)) {
        store.registerModule(CONTRACT_APP_STORE_MODULE_NAME, contractStoreModule);
      }
    });
    
    onUnmounted(() => {
      // 只有當模組是由這個組件註冊的才清理
      // 由於其他頁面可能也在使用，我們不在這裡取消註冊
    });

    const formData = ref({
      is_monthly: 1, // 預設為全月
      interest_type_id: 1, // 預設為息隨本減
      amount: null,
      period: null,
      annual_interest_rate: null,
      first_pay_date: new Date().toISOString().slice(0, 10),
      loan_date: new Date().toISOString().slice(0, 10)
    });

    const monthlyOptions = [
      { id: 1, name: "月供" },
      { id: 0, name: "半月供" }
    ];

    const interestTypeOptions = [
      { id: 1, name: "息隨本減" },
      { id: 2, name: "先息後本" }
    ];

    const scheduleFields = [
      { key: "period", label: "期數", class: "text-center" },
      { key: "pay_date", label: "供款日", class: "text-center" },
      { key: "initial_principal", label: "期初本金", class: "text-right" },
      { key: "principal", label: "每期本金", class: "text-right" },
      { key: "interest", label: "利息", class: "text-right" },
      { key: "payment_amount", label: "供款金額", class: "text-right" },
      { key: "remaining_principal", label: "尚欠本金", class: "text-right" }
    ];

    const isFormValid = computed(() => {
      return (
        formData.value.is_monthly !== null &&
        formData.value.interest_type_id !== null &&
        formData.value.amount > 0 &&
        formData.value.period > 0 &&
        formData.value.annual_interest_rate >= 0 &&
        formData.value.first_pay_date
      );
    });

    const totalInterest = computed(() => {
      if (!calculationResult.value || !calculationResult.value.schedules) return 0;
      return calculationResult.value.schedules.reduce((sum, schedule) => sum + schedule.interest, 0);
    });

    const firstPayDate = computed(() => {
      if (!calculationResult.value || !calculationResult.value.schedules || calculationResult.value.schedules.length === 0) {
        return "-";
      }
      return formatDate(calculationResult.value.schedules[0].pay_date);
    });

    const resetForm = () => {
      const today = new Date().toISOString().slice(0, 10);
      formData.value = {
        is_monthly: 1,
        interest_type_id: 1,
        amount: null,
        period: null,
        annual_interest_rate: null,
        first_pay_date: today,
        loan_date: today
      };
      calculationResult.value = null;
    };

    const calculateSchedule = async () => {
      if (!isFormValid.value) {
        toast({
          component: ToastificationContent,
          props: {
            title: "錯誤",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: "請填寫所有必填字段"
          }
        });
        return;
      }

      loading.value = true;
      try {
        // 再次確認 store module 存在
        if (!store.hasModule(CONTRACT_APP_STORE_MODULE_NAME)) {
          store.registerModule(CONTRACT_APP_STORE_MODULE_NAME, contractStoreModule);
        }
        
        const response = await root.$store.dispatch("app-contract/calculatePaymentSchedule", formData.value);
        
        // 檢查響應數據
        if (!response || !response.data) {
          throw new Error("API 回應數據格式不正確");
        }
        
        calculationResult.value = response.data;
        
        toast({
          component: ToastificationContent,
          props: {
            title: "成功",
            icon: "CheckIcon",
            variant: "success",
            text: "計算完成"
          }
        });
      } catch (error) {
        console.error("計算錯誤:", error);
        
        let errorMessage = "計算失敗，請檢查輸入參數";
        
        if (error.message && error.message.includes("unknown action type")) {
          errorMessage = "系統模組載入失敗，請重新整理頁面再試";
        } else if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast({
          component: ToastificationContent,
          props: {
            title: "錯誤",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: errorMessage
          }
        });
      } finally {
        loading.value = false;
      }
    };

    const goToContractCreate = () => {
      // 檢查是否有計算結果
      if (!calculationResult.value) {
        toast({
          component: ToastificationContent,
          props: {
            title: "錯誤",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: "請先進行計算再跳轉到合約頁面"
          }
        });
        return;
      }
      
      try {
        // 準備要傳遞的數據，只包含計算相關的字段，不包括放債人資料
        const contractData = {
          // 計算參數
          is_monthly: formData.value.is_monthly,
          interest_type_id: formData.value.interest_type_id,
          amount: formData.value.amount,
          period: formData.value.period,
          annual_interest_rate: formData.value.annual_interest_rate,
          first_pay_date: formData.value.first_pay_date,
          loan_date: formData.value.loan_date,
          // 計算結果 - 在 loan_information 中設置
          loan_information: {
            schedules: calculationResult.value.schedules || [],
            average_amount: calculationResult.value.average_amount,
            total_amount: calculationResult.value.total_amount,
            // 保留其他必要的預設值
            histories: []
          }
        };
        
        // 保存數據到 store
        root.$store.commit('app/updateContractDataFromLoan', contractData);
        
        // 檢查目前是否已經在合約創建頁面
        const currentRouteName = root.$router.currentRoute.name;
        
        if (currentRouteName === 'contracts-create') {
          // 如果已經在合約頁面，延遲發出事件通知頁面重新載入數據
          setTimeout(() => {
            root.$root.$emit('contract-data-updated');
          }, 300);
        } else {
          // 如果不在合約頁面，跳轉到合約創建頁面
          root.$router.push({ name: 'contracts-create' }).catch(err => {
            // 處理路由跳轉錯誤
            console.warn('路由跳轉警告:', err);
          });
        }
      } catch (error) {
        console.error('跳轉到合約頁面失敗:', error);
        toast({
          component: ToastificationContent,
          props: {
            title: "錯誤",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: "跳轉到合約頁面失敗，請重試"
          }
        });
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-HK');
    };

    return {
      formData,
      loading,
      calculationResult,
      monthlyOptions,
      interestTypeOptions,
      scheduleFields,
      isFormValid,
      totalInterest,
      firstPayDate,
      resetForm,
      calculateSchedule,
      goToContractCreate,
      formatDate
    };
  }
};
</script>

<style lang="scss" scoped>
// 僅針對此頁面的統計卡片緊湊樣式
.compact-card-body {
  padding: 0rem 1rem !important;
}

:deep(.table td) {
  vertical-align: middle;
}

:deep(.text-right) {
  text-align: right;
}

:deep(.text-center) {
  text-align: center;
}

// 響應式設計調整
@media (max-width: 768px) {
  .d-flex.justify-content-end {
    justify-content: center;
  }
  
  .compact-card-body {
    padding: 0rem 0.75rem !important;
  }
}
</style>